import type { AnchorHTMLAttributes, CSSProperties, ReactNode } from "react";
import { Link } from "react-router";
import { LineIcon } from "@/components/PlanIcon";
import { flagIconPaths } from "./flag-icons";

/*
  /women トップの共通部品。見た目の値は hp-base.ts の BASE_CSS (.hx- のクラス) が持つ。
  節を作るときは Sec で地と余白を決め、頭を SectionHead で組み、中身にほかの部品を置く。
  黒の面 (tone="dark") は黄を1面に1か所まで。明るい面の強調は青だけ。
*/

export type Tone = "cream" | "white" | "wash" | "dark" | "yellow" | "blue";

/**
 * 節の地と余白と幅。tone は cream、white、wash (青みの白)、dark (墨)、yellow (黄の全面)、blue (青の全面。上下を斜めに切る) から選ぶ。白を2つ続けない。
 * 隣の節とくっつけたいときは flush で上か下の余白を 0 にする。
 */
export function Sec({
  tone,
  id,
  flush,
  children,
}: {
  tone: Tone;
  id?: string;
  flush?: "top" | "bottom";
  children: ReactNode;
}) {
  const cls = ["hx-sec", `hx-${tone}`, flush === "top" ? "hx-pt0" : "", flush === "bottom" ? "hx-pb0" : ""]
    .filter(Boolean)
    .join(" ");
  return (
    <section id={id} className={cls}>
      <div className="hx-wrap">{children}</div>
    </section>
  );
}

/** 青い旗の札。節の頭の日本語の短い言葉。両端が旗のように切れる (LP の Ribbon と同じ) */
export function Flag({ children, className }: { children: ReactNode; className?: string }) {
  const paths = typeof children === "string" ? flagIconPaths(children) : undefined;
  return (
    <p className={`hx-flag ${className ?? ""}`}>
      {paths ? <LineIcon paths={paths} className="hx-flag-ic" size={15} /> : null}
      {children}
    </p>
  );
}

type SectionHeadProps = {
  /** 青い旗の札に出す日本語の短い言葉 */
  label: string;
  lede?: ReactNode;
  /** 見出しの大きさ。省く (大) は主な節、"s" (小) は補助の節。箱の見出しは大だけ */
  size?: "s";
} & (
  | { children: ReactNode; bands?: never; strike?: never; caret?: never }
  /** 黄の箱の見出し (中央そろえ)。h2 の文を2つに分けて渡す。行ごとに墨の字の黄の箱 */
  | { bands: readonly [ReactNode, ReactNode?]; children?: never; strike?: never; caret?: never }
  /**
   * 常識を赤線で消す見出し (中央そろえ)。strike に h2 の中身を渡し、消す言葉は <del>、答えは <mark className="hx-hl"> で書く。
   * caret は見出しの下に赤字で出る答えの向きの1行 (「↓」は付けて渡す)。明るい面 (white、cream) だけで使う。
   */
  | { strike: ReactNode; caret: ReactNode; children?: never; bands?: never }
);

/**
 * 節の頭。label は青い旗の札、children は明朝の見出し、lede は太字の1文。
 * 箱の見出しにするときは children の代わりに bands、赤線で消す見出しにするときは strike と caret を渡す。
 * 見出しは h2。ページの中に h2 以外の見出しを作らない。
 */
export function SectionHead({ label, children, bands, strike, caret, lede, size }: SectionHeadProps) {
  return (
    <div className={bands || strike ? "hx-head-c" : "hx-head"}>
      <Flag>{label}</Flag>
      {strike ? (
        <>
          <h2 className="hx-h2 hx-strike">{strike}</h2>
          <p className="hx-caret">{caret}</p>
        </>
      ) : bands ? (
        <h2 className="hx-h2 hx-bands">
          {/* 中の hx-bt は動きの字の入れ物 (motion.ts)。箱が伸びてから字が出る */}
          <span className="hx-bb">
            <span className="hx-bt">{bands[0]}</span>
          </span>
          {bands[1] && (
            <span className="hx-br">
              <span className="hx-bt">{bands[1]}</span>
            </span>
          )}
        </h2>
      ) : (
        <h2 className={size === "s" ? "hx-h2 hx-h2-s" : "hx-h2"}>{children}</h2>
      )}
      {lede && <p className="hx-lede">{lede}</p>}
    </div>
  );
}

/**
 * ブラウザの窓の枠。本物の画面や表を入れる。label は上の細い帯に出る字 (飾りなので読み上げない)。
 * footer を渡すと窓の下に細い帯で出る。
 */
export function BrowserFrame({
  label,
  children,
  footer,
  className,
}: {
  label?: string;
  children: ReactNode;
  footer?: ReactNode;
  className?: string;
}) {
  return (
    <div className={`hx-frame ${className ?? ""}`}>
      <div className="hx-frame-bar" aria-hidden="true">
        <i />
        <i />
        <i />
        {label && <span className="hx-frame-label">{label}</span>}
      </div>
      {children}
      {footer}
    </div>
  );
}

/**
 * 端から端までの写真の帯。src は /images/hero-*.webp か /assets/ の /women の素材だけ。
 * children を渡すと短い明朝の一言が下に重なり、字の下だけ暗くなる。写真が発言者や本人でないときは credit に断りを書く。
 * focus は object-position (例 "50% 20%")。
 */
export function PhotoBand({
  src,
  focus,
  children,
  credit,
  className,
}: {
  src: string;
  focus?: string;
  children?: ReactNode;
  credit?: string;
  className?: string;
}) {
  const shaded = Boolean(children || credit);
  return (
    <figure
      className={`hx-band ${shaded ? "hx-band-shade" : ""} ${className ?? ""}`}
      style={focus ? ({ "--focus": focus } as CSSProperties) : undefined}
    >
      <img src={src} alt="" width={1500} height={1000} loading="lazy" decoding="async" />
      {shaded && (
        <div className="hx-band-in">
          <div className="hx-wrap">
            {children && <p className="hx-band-line">{children}</p>}
            {credit && <p className="hx-band-credit">{credit}</p>}
          </div>
        </div>
      )}
    </figure>
  );
}

/**
 * 大きな引用。1節に1つ。children は引用の本文、who は発言者。
 */
export function PullQuote({ children, who }: { children: ReactNode; who: string }) {
  return (
    <figure className="hx-pull">
      <blockquote>{children}</blockquote>
      <figcaption className="hx-who">{who}</figcaption>
    </figure>
  );
}

/**
 * 並べる引用の1つ。topic は設問から起こした見出し、who は発言者。
 * 本文の太字は strong で渡すと青みのマーカーが敷かれる。親は ul などにせず div で並べ、最初の1つだけ上に墨の罫を付ける (.hx-qi の親で指定)。
 */
export function Quote({ topic, children, who }: { topic: string; children: ReactNode; who: string }) {
  return (
    <figure className="hx-qi">
      <figcaption className="hx-qi-tp">{topic}</figcaption>
      <blockquote>{children}</blockquote>
      <p className="hx-who">{who}</p>
    </figure>
  );
}

export const ARROW = (
  <svg className="hx-arrow" viewBox="0 0 34 20" aria-hidden="true">
    <path
      d="M2 10h28M21 2l9 8-9 8"
      fill="none"
      stroke="currentColor"
      strokeWidth="3.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

type PillProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "href"> & {
  /** 黒の面は onDark (白の塗り)、明るい面は onLight (墨の塗り) */
  tone: "onDark" | "onLight";
  children: ReactNode;
  /** 字の前に置く図記号 (NavIcon) */
  icon?: ReactNode;
  /** 外へ出るリンク。新しいタブで開く */
  href?: string;
  /** /women の中のリンク */
  to?: string;
  [dataAttr: `data-${string}`]: string | undefined;
};

/**
 * 申し込みの札。href は外へ出るリンク (新しいタブで開く)、to は /women の中のリンク。
 * 計測の属性 (data-cta-id など) と onClick はそのまま渡せる。
 */
export function ApplyPill({ tone, children, icon, href, to, className, ...rest }: PillProps) {
  const cls = `hx-pill hx-pill-${tone} ${className ?? ""}`;
  const inner = (
    <>
      {icon}
      <span>{children}</span>
      {ARROW}
    </>
  );
  if (to) {
    return (
      <Link to={to} className={cls} {...rest}>
        {inner}
      </Link>
    );
  }
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className={cls} {...rest}>
      {inner}
    </a>
  );
}

/** 署名「実践主義。」とロゴ。黒の面の締めに置く。 */
export function Signature() {
  return (
    <p className="hx-sign">
      <span>実践主義。</span>
      <img src="/assets/yumesaku_enginner_logo.svg" alt="ゆめさくエンジニア" width={968} height={175} />
    </p>
  );
}
